import { collection, doc, onSnapshot, query, where, runTransaction, serverTimestamp, getDocsFromServer, getDocFromServer } from 'firebase/firestore';
import db from '../firebaseConfig';
import { requireProductActor } from './productPersistence';
import { getCollectionName, VALID_COUNTRY_IDS } from './countryConfig';
import { getProductVariants, mergeVariantSelection, createVariantId } from './productVariants';
import { loadProductExportContexts } from './productExportContext';

const checkCountry = country => { if (!VALID_COUNTRY_IDS.includes(country)) throw new Error('無效的國家'); };
const checkItemCountry = (item, id, country) => {
  if (item.country !== country || !item.productId || item.productId !== id) throw new Error('備播關聯國家或商品不符，請確認資料');
};
const checkProductCountry = (product, country) => {
  if (product.country !== undefined && product.country !== country) throw new Error('商品國家不符，請確認資料');
};
const active = (snapshot, country) => {
  if (!snapshot.exists() || snapshot.data().country !== country || snapshot.data().archived) throw new Error('清單已刪除、不存在或國家不符，請重新選擇');
};
const touchList = (tx, ref, list, actor) => tx.update(ref, { revision: (Number(list.revision) || 0) + 1, updatedAt: serverTimestamp(), updatedBy: actor });
const sameSelection = (a = [], b = []) => a.length === b.length && a.every(id => b.includes(id));
export const subscribeLivestreamLists = (country, callback, onError, { includeArchived = false } = {}) => {
  checkCountry(country);
  return onSnapshot(query(collection(db, 'livestreamLists'), where('country', '==', country)), snapshot => callback(snapshot.docs.map(d => ({ ...d.data(), id: d.id })).filter(list => list.country === country && (includeArchived || !list.archived)).sort((a, b) => a.name.localeCompare(b.name))), onError);
};
export const subscribeListItems = (country, listId, callback, onError) => {
  checkCountry(country);
  let stopItems = () => {}, generation = 0;
  const stopList = onSnapshot(doc(db, 'livestreamLists', listId), parent => {
    stopItems(); const serial = ++generation; callback([], false);
    try { active(parent, country); } catch (error) { onError?.(error); return; }
    stopItems = onSnapshot(collection(db, 'livestreamLists', listId, 'items'), snapshot => {
      if (serial !== generation) return;
      try {
        snapshot.docs.forEach(item => checkItemCountry(item.data(), item.id, country));
        callback(snapshot.docs.map(d => ({ ...d.data(), id: d.id })));
      } catch (error) { callback([], false); onError?.(error); }
    }, error => { if (serial === generation) { callback([], false); onError?.(error); } });
  }, error => { stopItems(); generation += 1; callback([], false); onError?.(error); });
  return () => { generation += 1; stopList(); stopItems(); };
};
export const newLivestreamListId = () => doc(collection(db, 'livestreamLists')).id;
export const createLivestreamList = async (country, name, id = newLivestreamListId()) => {
  checkCountry(country);
  const actor = await requireProductActor();
  const cleanName = name.trim();
  if (!cleanName || cleanName.length > 100) throw new Error('請輸入 1–100 字的清單名稱');
  const ref = doc(db, 'livestreamLists', id);
  await runTransaction(db, async tx => {
    const existing = await tx.get(ref);
    if (existing.exists()) {
      if (existing.data().country === country && existing.data().name === cleanName && !existing.data().archived) return;
      throw new Error('先前的建立已完成但內容不同，請先查看清單');
    }
    tx.set(ref, { country, name: cleanName, revision: 1, createdAt: serverTimestamp(), createdBy: actor, updatedAt: serverTimestamp(), updatedBy: actor });
  });
  return ref.id;
};
export const renameLivestreamList = async (country, list, name) => {
  checkCountry(country);
  if (list.country !== country) throw new Error('清單國家不符');
  const actor = await requireProductActor();
  const cleanName = name.trim();
  if (!cleanName || cleanName.length > 100) throw new Error('請輸入 1–100 字的清單名稱');
  const ref = doc(db, 'livestreamLists', list.id);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    active(snap, list.country);
    if (snap.data().name !== list.name) throw new Error('清單名稱已被修改，請重新確認');
    tx.update(ref, { name: cleanName, revision: (snap.data().revision || 0) + 1, updatedAt: serverTimestamp(), updatedBy: actor });
  });
};
export const addProductToList = async ({ listId, country, product, selectedVariantIds }) => {
  checkCountry(country);
  checkProductCountry(product, country);
  const actor = await requireProductActor();
  const listRef = doc(db, 'livestreamLists', listId);
  const itemRef = doc(listRef, 'items', product.id);
  const productRef = doc(db, getCollectionName(country), product.id);
  await runTransaction(db, async tx => {
    const [list, item, liveProduct] = await Promise.all([tx.get(listRef), tx.get(itemRef), tx.get(productRef)]);
    active(list, country);
    if (item.exists() && (item.data().country !== country || item.data().productId !== product.id)) throw new Error('清單商品國家或識別不符');
    if (!liveProduct.exists()) throw new Error('商品已不存在');
    checkProductCountry(liveProduct.data(), country);
    const variants = getProductVariants(liveProduct.data());
    if (selectedVariantIds.some(id => !variants.some(v => v.id === id && v.active))) throw new Error('款式已變更或停用，請重新選款');
    if (variants.some(v => v.active) && !selectedVariantIds.length) throw new Error('請至少選一個款式');
    const merged = mergeVariantSelection(item.data()?.selectedVariantIds, selectedVariantIds);
    if (item.exists() && sameSelection(item.data().selectedVariantIds, merged)) return;
    tx.set(itemRef, { country, productId: product.id, selectedVariantIds: merged, revision: (item.data()?.revision || 0) + 1, updatedAt: serverTimestamp(), updatedBy: actor, ...(item.exists() ? {} : { membershipId: createVariantId(), createdAt: serverTimestamp(), createdBy: actor }) }, { merge: true });
    touchList(tx, listRef, list.data(), actor);
    // Freeze legacy IDs before later edits change labels/barcodes.
    if (!liveProduct.data().styleIds) tx.update(productRef, { styleIds: variants.map(v => v.id), revision: (Number(liveProduct.data().revision) || 0) + 1, updatedBy: actor, updatedAt: serverTimestamp() });
  });
};
export const updateListSelection = async ({ country, listId, item, selectedVariantIds, remove = false }) => {
  checkCountry(country);
  if (item.country !== country) throw new Error('備播關聯國家不符');
  const actor = await requireProductActor();
  const ref = doc(db, 'livestreamLists', listId, 'items', item.productId);
  await runTransaction(db, async tx => {
    const listRef = doc(db, 'livestreamLists', listId);
    const [snap, list] = await Promise.all([tx.get(ref), tx.get(listRef)]);
    active(list, item.country);
    if (!snap.exists() && remove) return;
    if (!snap.exists() || snap.data().country !== item.country || snap.data().productId !== item.productId
      || snap.data().membershipId !== item.membershipId) throw new Error('這項商品關聯已變更，請重新確認');
    if (snap.data().revision !== item.revision) {
      if (!remove && sameSelection(snap.data().selectedVariantIds, [...new Set(selectedVariantIds)])) return;
      throw new Error('這項商品已被其他人修改，請重新確認目前款式');
    }
    if (remove) { tx.delete(ref); touchList(tx, listRef, list.data(), actor); return; }
    const product = await tx.get(doc(db, getCollectionName(item.country), item.productId));
    if (!product.exists()) throw new Error('商品已變更，請重新確認');
    checkProductCountry(product.data(), country);
    const variants = getProductVariants(product.data());
    if (selectedVariantIds.some(id => !variants.some(v => v.id === id && v.active))) throw new Error('款式已變更或停用，請重新選款');
    if (variants.some(v => v.active) && !selectedVariantIds.length) throw new Error('請至少選一個款式');
    if (sameSelection(snap.data().selectedVariantIds, [...new Set(selectedVariantIds)])) return;
    tx.update(ref, { selectedVariantIds: [...new Set(selectedVariantIds)], revision: item.revision + 1, updatedAt: serverTimestamp(), updatedBy: actor });
    touchList(tx, listRef, list.data(), actor);
  });
};
export const loadListExportRows = async (country, listId, { includeDetails = false } = {}) => {
  checkCountry(country);
  const list = await getDocFromServer(doc(db, 'livestreamLists', listId));
  active(list, country);
  const items = await getDocsFromServer(collection(db, 'livestreamLists', listId, 'items'));
  items.docs.forEach(item => checkItemCountry(item.data(), item.id, country));
  const rows = [];
  for (const item of items.docs) {
    const data = item.data();
    const product = await getDocFromServer(doc(db, getCollectionName(country), data.productId));
    if (!product.exists()) throw new Error(`商品 ${data.productId} 已不存在，請先從清單處理此項目`);
    checkProductCountry(product.data(), country);
    if ((data.selectedVariantIds || []).some(id => !getProductVariants(product.data()).some(v => v.id === id))) throw new Error('備播款式已變更，請重新選款');
    rows.push({ country, product: { ...product.data(), id: product.id }, selectedVariantIds: data.selectedVariantIds || [] });
  }
  if (includeDetails) await loadProductExportContexts(country, rows);
  const current = await getDocFromServer(doc(db, 'livestreamLists', listId));
  active(current, country);
  if (current.data().revision !== list.data().revision) throw new Error('清單已變更，請重新匯出');
  return rows;
};

// No collection-group index or new collection: read this product's item document
// in each existing same-country list, and retain normal authenticated rules.
export const subscribeProductListMemberships = (country, productId, callback, onError) => {
  checkCountry(country);
  let children = [], generation = 0;
  const stop = subscribeLivestreamLists(country, lists => {
    const serial = ++generation;
    children.forEach(unsubscribe => unsubscribe()); children = [];
    const items = new Map(), waiting = new Set(lists.map(list => list.id));
    const emit = () => callback({ lists, memberships: lists.filter(list => items.has(list.id)).map(list => ({ list, item: items.get(list.id) })), ready: waiting.size === 0 });
    emit();
    children = lists.map(list => onSnapshot(doc(db, 'livestreamLists', list.id, 'items', productId), snapshot => {
      if (serial !== generation) return;
      if (snapshot.exists()) {
        const item = snapshot.data();
        if (item.country !== country || item.productId !== productId) { onError?.(new Error('備播關聯國家或商品不符，請先確認資料')); return; }
        items.set(list.id, { ...item, id: snapshot.id });
      } else items.delete(list.id);
      waiting.delete(list.id); emit();
    }, error => { if (serial === generation) onError?.(error); }));
  }, onError);
  return () => { generation += 1; stop(); children.forEach(unsubscribe => unsubscribe()); };
};

export const prepareListArchive = async (country, listId) => {
  checkCountry(country); await requireProductActor();
  const ref = doc(db, 'livestreamLists', listId);
  const before = await getDocFromServer(ref); active(before, country);
  const items = await getDocsFromServer(collection(ref, 'items'));
  items.docs.forEach(item => checkItemCountry(item.data(), item.id, country));
  const after = await getDocFromServer(ref); active(after, country);
  if (before.data().revision !== after.data().revision) throw new Error('清單在讀取期間已變更，請重新確認後刪除');
  return { list: { ...after.data(), id: listId }, count: items.size };
};
export const setListArchived = async ({ country, list, archived }) => {
  checkCountry(country);
  if (list.country !== country) throw new Error('清單國家不符');
  const actor = await requireProductActor();
  const ref = doc(db, 'livestreamLists', list.id);
  await runTransaction(db, async tx => {
    const current = await tx.get(ref);
    if (!current.exists() || current.data().country !== list.country) throw new Error('清單已不存在或國家不符');
    if (Boolean(current.data().archived) === archived) return; // Safe repeat after uncertain success.
    if (current.data().revision !== list.revision || current.data().name !== list.name) throw new Error('清單已被其他人修改，請重新確認名稱及商品數量');
    tx.update(ref, { archived, archivedAt: archived ? serverTimestamp() : null, archivedBy: archived ? actor : null,
      revision: (Number(current.data().revision) || 0) + 1, updatedAt: serverTimestamp(), updatedBy: actor });
    // Keep every child item; restoring the parent restores all its associations.
  });
};
